import type { ReactNode } from 'react';

/**
 * 칩 (19회차, 콘솔 검토 D-3). 크기는 18회차까지의 값 그대로다.
 *
 * 상태·타입처럼 한 낱말짜리 값을 감싼다. 색만으로 뜻을 전하지 않는다 —
 * 칩 안에는 항상 글자가 들어간다. 색은 읽는 속도를 돕는 보조 수단이다.
 */
export type ChipTone =
  | 'ok'
  | 'fail'
  | 'error'
  | 'http'
  | 'sse'
  | 'mcp'
  | 'worker'
  | 'pdf'
  | 'slow'
  | 'neutral';

const TONE: Record<ChipTone, string> = {
  ok: 'bg-ok/10 text-ok ring-ok/25',
  fail: 'bg-fail/10 text-fail ring-fail/25',
  error: 'bg-error/10 text-error ring-error/25',
  http: 'bg-http/10 text-http ring-http/25',
  sse: 'bg-sse/10 text-sse ring-sse/25',
  mcp: 'bg-mcp/10 text-mcp ring-mcp/25',
  worker: 'bg-worker/10 text-worker ring-worker/25',
  pdf: 'bg-pdf/10 text-pdf ring-pdf/25',
  slow: 'bg-slow/10 text-slow ring-slow/25',
  neutral: 'bg-band text-muted ring-hair',
};

export function Chip({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: ChipTone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset ${TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * 범례 표식 (콘솔 검토 D-3).
 *
 * X-View 범례가 색 점만 쓰면 2색형 색각에서 계열을 못 가른다. 색과 **모양**을 같이 쓴다.
 * 캔버스 점도 같은 모양으로 찍는다(`xview/renderer.ts`의 `SHAPE`).
 */
export type MarkShape = 'circle' | 'square' | 'triangle' | 'diamond' | 'cross' | 'x';

export function Mark({ color, shape }: { color: string; shape: MarkShape }) {
  const common = { fill: color, stroke: 'none' } as const;
  return (
    <svg aria-hidden="true" viewBox="0 0 10 10" className="size-2.5 shrink-0">
      {shape === 'circle' ? <circle cx="5" cy="5" r="4.5" {...common} /> : null}
      {shape === 'square' ? <rect x="0.5" y="0.5" width="9" height="9" {...common} /> : null}
      {shape === 'triangle' ? <polygon points="5,0 10,10 0,10" {...common} /> : null}
      {shape === 'diamond' ? <polygon points="5,0 10,5 5,10 0,5" {...common} /> : null}
      {shape === 'cross' ? (
        <polygon
          points="3.5,0 6.5,0 6.5,3.5 10,3.5 10,6.5 6.5,6.5 6.5,10 3.5,10 3.5,6.5 0,6.5 0,3.5 3.5,3.5"
          {...common}
        />
      ) : null}
      {shape === 'x' ? (
        <path d="M1 1 9 9M9 1 1 9" fill="none" stroke={color} strokeWidth="2.5" />
      ) : null}
    </svg>
  );
}
